import { action, observable } from 'mobx';
import * as Models from 'Models/Entities';
import {
	attribute,
	entity,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserReportRequest from 'Models/Security/Acl/UserReportRequest';
import VisitorsReportRequest from 'Models/Security/Acl/VisitorsReportRequest';
import { IAcl } from 'Models/Security/IAcl';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import * as Validators from 'Validators';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

export interface IReportRequestEntityAttributes extends IModelAttributes {
	paymentTransactionId: string;
	transactionAmount: number;
	requestContent: string;

	businessEntityCreditReportSettingsId: string;
	businessEntityCreditReportSettings: Models.BusinessEntityCreditReportSettingsEntity | Models.IBusinessEntityCreditReportSettingsEntityAttributes;
	creditReportId?: string;
	creditReport?: Models.CreditReportEntity | Models.ICreditReportEntityAttributes;
	customerId?: string;
	customer?: Models.CustomerEntity | Models.ICustomerEntityAttributes;
	customerApplicationId?: string;
	paymentStatusId: string;
	paymentStatus: Models.PaymentStatusEntity | Models.IPaymentStatusEntityAttributes;
	userId?: string;
	user?: Models.UserEntity | Models.IUserEntityAttributes;
}

@entity('ReportRequestEntity', 'Report Request')
export default class ReportRequestEntity extends Model
	implements IReportRequestEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserReportRequest(),
		new VisitorsReportRequest(),
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		
		
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		
		
	];

	
	/**
	 * Stripe Transaction id
	 */
	@observable
	@attribute<ReportRequestEntity, string>()
	@CRUD({
		name: 'Payment TransactionId',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public paymentTransactionId: string;
	

	
	@Validators.Numeric()
	@observable
	@attribute<ReportRequestEntity, number>()
	@CRUD({
		name: 'Transaction Amount',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public transactionAmount: number;

	@observable
	@attribute<ReportRequestEntity, string>()
	@CRUD({
		name: 'Request Content',
		displayType: 'textarea',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public requestContent: string;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Business Entity Credit Report Settings',
		displayType: 'reference-combobox',
		order: 50,
		referenceTypeFunc: () => Models.BusinessEntityCreditReportSettingsEntity,
		
	})
	public businessEntityCreditReportSettingsId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public businessEntityCreditReportSettings: Models.BusinessEntityCreditReportSettingsEntity;

	@observable
	@attribute()
	@CRUD({
		
		name: 'Credit Report',
		displayType: 'reference-combobox',
		order: 60,
		referenceTypeFunc: () => Models.CreditReportEntity,
		
	})
	public creditReportId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public creditReport: Models.CreditReportEntity;

	@observable
	@attribute()
	@CRUD({
		
		name: 'Customer',
		displayType: 'reference-combobox',
		order: 70,
		referenceTypeFunc: () => Models.CustomerEntity,
		
	})
	public customerId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public customer: Models.CustomerEntity;

	@observable
	@attribute<ReportRequestEntity, string>()
	public customerApplicationId?: string;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Payment Status',
		displayType: 'reference-combobox',
		order: 80,
		referenceTypeFunc: () => Models.PaymentStatusEntity,
	})
	public paymentStatusId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public paymentStatus: Models.PaymentStatusEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'User',
		displayType: 'reference-combobox',
		order: 90,
		referenceTypeFunc: () => Models.UserEntity,
		
	})
	public userId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user?: Models.UserEntity;


	constructor(attributes?: Partial<IReportRequestEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IReportRequestEntityAttributes>) {
		
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.paymentTransactionId !== undefined) {
				this.paymentTransactionId = attributes.paymentTransactionId;
			}
			if (attributes.transactionAmount !== undefined) {
				this.transactionAmount = attributes.transactionAmount;
			}
			if (attributes.requestContent !== undefined) {
				this.requestContent = attributes.requestContent;
			}
			if (attributes.businessEntityCreditReportSettingsId !== undefined) {
				this.businessEntityCreditReportSettingsId = attributes.businessEntityCreditReportSettingsId;
			}
			if (attributes.businessEntityCreditReportSettings !== undefined) {
				if (attributes.businessEntityCreditReportSettings === null) {
					this.businessEntityCreditReportSettings = attributes.businessEntityCreditReportSettings;
				} else if (attributes.businessEntityCreditReportSettings instanceof Models.BusinessEntityCreditReportSettingsEntity) {
					this.businessEntityCreditReportSettings = attributes.businessEntityCreditReportSettings;
					this.businessEntityCreditReportSettingsId = attributes.businessEntityCreditReportSettings.id;
				} else {
					this.businessEntityCreditReportSettings = new Models.BusinessEntityCreditReportSettingsEntity(attributes.businessEntityCreditReportSettings);
					this.businessEntityCreditReportSettingsId = this.businessEntityCreditReportSettings.id;
				}
			}
			if (attributes.creditReportId !== undefined) {
				this.creditReportId = attributes.creditReportId;
			}
			if (attributes.creditReport !== undefined) {
				if (attributes.creditReport === null) {
					this.creditReport = attributes.creditReport;
				} else if (attributes.creditReport instanceof Models.CreditReportEntity) {
					this.creditReport = attributes.creditReport;
					this.creditReportId = attributes.creditReport.id;
				} else {
					this.creditReport = new Models.CreditReportEntity(attributes.creditReport);
					this.creditReportId = this.creditReport.id;
				}
			}
			if (attributes.customerId !== undefined) {
				this.customerId = attributes.customerId;
			}
			if (attributes.customer !== undefined) {
				if (attributes.customer === null) {
					this.customer = attributes.customer;
				} else if (attributes.customer instanceof Models.CustomerEntity) {
					this.customer = attributes.customer;
					this.customerId = attributes.customer.id;
				} else {
					this.customer = new Models.CustomerEntity(attributes.customer);
					this.customerId = this.customer.id;
				}
			}
			if (attributes.customerApplicationId !== undefined) {
				this.customerApplicationId = attributes.customerApplicationId;
			}
			if (attributes.paymentStatusId !== undefined) {
				this.paymentStatusId = attributes.paymentStatusId;
			}
			if (attributes.paymentStatus !== undefined) {
				if (attributes.paymentStatus === null) {
					this.paymentStatus = attributes.paymentStatus;
				} else if (attributes.paymentStatus instanceof Models.PaymentStatusEntity) {
					this.paymentStatus = attributes.paymentStatus;
					this.paymentStatusId = attributes.paymentStatus.id;
				} else {
					this.paymentStatus = new Models.PaymentStatusEntity(attributes.paymentStatus);
					this.paymentStatusId = this.paymentStatus.id;
				}
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	
	public defaultExpands = `
		businessEntityCreditReportSettings {
			${Models.BusinessEntityCreditReportSettingsEntity.getAttributes().join('\n')}
		}
		creditReport {
			${Models.CreditReportEntity.getAttributes().join('\n')}
		}
		customer {
			${Models.CustomerEntity.getAttributes().join('\n')}
		}
		paymentStatus {
			${Models.PaymentStatusEntity.getAttributes().join('\n')}
		}
		user {
			${Models.UserEntity.getAttributes().join('\n')}
		}
	`;
	

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(ReportRequestEntity.prototype, 'created');
CRUD(modifiedAttr)(ReportRequestEntity.prototype, 'modified');
